// InboxPanel — the DB-authoritative agent task inbox (human-in-the-loop), as a
// reusable surface. Rendered both as the full /inbox page (with a heading) and
// inside the header's Action Required blade (no heading — the Blade chrome
// supplies the title), so the user can triage without losing their place.
//
// RELIABILITY IS THE POINT. The full pending set is ALWAYS reseeded straight from
// the database (GET /api/agents/inbox):
//   * on first load,
//   * on every manual Refresh,
//   * AND on every SignalR (re)connect (keyed off the agent connection's
//     `connectionEpoch`, which bumps on each connect/reconnect),
//   * plus a light re-query whenever ANY agent event arrives.
// SignalR is only a refresh TRIGGER, never the delivery channel — a dropped or
// missed push can never lose an item, because the next reseed re-queries the DB
// and re-surfaces the full set.
//
// Responding (approve / deny / answer) writes the response and re-dispatches a
// continuation server-side; we then reseed so the resolved item drops off.
//
// onCountChange fires after every reseed with the current pending count so an
// owner (the header badge) can stay in lockstep — including right after a
// response resolves an item.
//
// Orion Pulsars: proposal batches and questions are listed too (a distinct item
// type, see PulsarInboxCards). "Review" opens the review surface — in place of
// the list inside the Action Required blade (a blade-in-a-blade would fight over
// focus trapping), or in a Blade on the full /inbox page — and closing it returns
// focus to the item's Review button.

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TextArea } from '@progress/kendo-react-inputs';
import type { AgentInboxItem, AttentionItem } from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { useAgentActivity } from '../../lib/agentActivity';
import { cancelAgentRun, cancelWorkflowRun, respondToAgentRun } from '../../lib/agentApi';
import { actionItemCount, loadActionItems } from '../../lib/actionItems';
import { ApiError } from '../../lib/apiClient';
import { PageHeader } from '../../components/PageHeader';
import { ArgsView } from '../../components/ArgsView';
import { Blade } from '../../components/Blade';
import { AuthContext } from '../../lib/auth';
import { ProposalReview } from '../StandingAgents/review/ProposalReview';
import { canApprove as roleCanApprove } from '../StandingAgents/pulsarUi';
import { PulsarBatchCard, PulsarQuestionCard } from './PulsarInboxCards';
import './Inbox.css';

export interface InboxPanelProps {
  /** Optional heading rendered in the control row (page only; the blade omits it). */
  title?: ReactNode;
  /**
   * 'page' renders the standard PageHeader (full-page /inbox); 'blade' (default)
   * renders the compact control-row head used inside the header blade.
   */
  variant?: 'page' | 'blade';
  /** Called after every reseed with the current pending count (badge sync). */
  onCountChange?: (count: number) => void;
  /**
   * Called when the user follows an item's history link, so a host (the bubble's
   * blade) can dismiss itself — otherwise the run history opens hidden behind it.
   * The full-page /inbox variant has nothing to dismiss and omits it.
   */
  onNavigate?: () => void;
}

export function InboxPanel({ title, variant = 'blade', onCountChange, onNavigate }: InboxPanelProps) {
  const agent = useAgentActivity();
  // Role gates the pulsar approve controls (the server enforces it regardless).
  // Read the context directly so the panel still renders outside an AuthProvider.
  const auth = useContext(AuthContext);
  const role = auth?.state.status === 'authenticated' ? auth.state.user.role : null;
  const approver = roleCanApprove(role);
  const [items, setItems] = useState<AgentInboxItem[] | null>(null);
  const [pulsarItems, setPulsarItems] = useState<AttentionItem[]>([]);
  // The batch open in the review surface; closing it refocuses its Review button.
  const [reviewing, setReviewing] = useState<AttentionItem | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [pendingId, setPendingId] = useState<string | null>(null);
  // The item awaiting "Approve all & future" confirmation (the run-wide auto-approve
  // grant is high-blast-radius, so we make the operator confirm it explicitly).
  const [confirmFuture, setConfirmFuture] = useState<AgentInboxItem | null>(null);
  // The workflow-launched item awaiting "Cancel run" confirmation. A canvas-bound
  // agent run can't be cancelled on its own — cancelling cascades to the WHOLE
  // owning workflow run — so the operator confirms that blast radius explicitly.
  const [confirmCancel, setConfirmCancel] = useState<AgentInboxItem | null>(null);
  // Avoid overlapping reseeds (event + epoch can fire close together).
  const inFlight = useRef(false);
  // A trigger that arrives while a reseed is in flight must NOT be dropped — the
  // DB query is the source of truth, so we remember it and run exactly one trailing
  // reseed when the current one finishes. Without this, a reseed requested mid-flight
  // (e.g. the post-response sync) is lost, leaving a just-resolved item on screen and
  // the count stale until some later unrelated event.
  const pendingReseed = useRef(false);
  // Keep the latest callback without re-identifying reseed (so the agent-event
  // subscription below doesn't churn when the parent passes an inline function).
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  }, [onCountChange]);

  // THE reseed: the single source of truth for the inbox is the DB query.
  const reseed = useCallback(async () => {
    if (inFlight.current) {
      pendingReseed.current = true;
      return;
    }
    inFlight.current = true;
    try {
      // Loop (rather than self-recurse) so a trigger that arrives mid-fetch runs one
      // more pass: the DB query is the source of truth and must never be skipped.
      do {
        pendingReseed.current = false;
        try {
          const data = await loadActionItems();
          setItems(data.runs);
          setPulsarItems(data.pulsar);
          setError(null);
          onCountChangeRef.current?.(actionItemCount(data));
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load the inbox.');
        }
      } while (pendingReseed.current);
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Reseed on first load AND on every (re)connect (connectionEpoch bumps each time).
  // This is what makes a missed push harmless: a reconnect always re-queries the DB.
  /* eslint-disable react-hooks/exhaustive-deps --
     reseed() only setState()s AFTER an awaited fetch (external sync), and we want
     this to fire ONLY on a connection-epoch change (a (re)connect), not on every
     reseed identity change — that's the whole no-missed-alerts trigger. */
  useEffect(() => {
    void reseed();
  }, [agent?.connectionEpoch]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Any live agent event is only a "something changed" nudge → re-query the DB.
  useEffect(() => {
    if (!agent) return undefined;
    return agent.onAgentActivity(() => {
      void reseed();
    });
  }, [agent, reseed]);

  const respond = useCallback(
    async (
      item: AgentInboxItem,
      decision: 'answer' | 'approve' | 'deny',
      // "Approve all & future for this task": carried only on an approve. Auto-approves
      // every later gated tool call for the rest of THIS run, so it stops re-parking.
      approveFuture = false,
    ) => {
      setPendingId(item.agentRunId);
      setError(null);
      try {
        await respondToAgentRun(item.agentRunId, {
          decision,
          answer: decision === 'answer' ? (answers[item.agentRunId] ?? '') : null,
          approveFuture: decision === 'approve' ? approveFuture : undefined,
        });
        setAnswers((a) => {
          const next = { ...a };
          delete next[item.agentRunId];
          return next;
        });
        await reseed();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send the response.');
      } finally {
        setPendingId(null);
      }
    },
    [answers, reseed],
  );

  // Cancel a parked run instead of responding to it — the run is finalized
  // (Cancelled) server-side and drops off the inbox on the next reseed.
  //
  // Two paths, chosen by whether the run is canvas-bound:
  //   * Ad-hoc / copilot run (no workflowRunId) → soft-cancel just that run.
  //   * Workflow-launched AgentActivity run → the agent-run cancel endpoint
  //     refuses those (409) because the run is tied to a suspended workflow
  //     bookmark; cancel the OWNING workflow run instead, which cascades to this
  //     parked child and finalizes the bookmark in one transaction.
  const doCancel = useCallback(
    async (item: AgentInboxItem) => {
      const canvasBound = item.workflowRunId != null && item.workflowId != null;
      setPendingId(item.agentRunId);
      setError(null);
      try {
        if (canvasBound) {
          await cancelWorkflowRun(item.workflowId!, item.workflowRunId!);
        } else {
          await cancelAgentRun(item.agentRunId);
        }
        await reseed();
      } catch (err) {
        // A 409 means the run already reached a terminal state elsewhere (e.g. it
        // finished or was cancelled in a race). Reseed so the now-stale card drops
        // off rather than leaving it (and the badge count) wrong until some later
        // unrelated refresh.
        if (err instanceof ApiError && err.status === 409) {
          await reseed();
        } else {
          setError(err instanceof Error ? err.message : 'Failed to cancel the run.');
        }
      } finally {
        setPendingId(null);
      }
    },
    [reseed],
  );

  // Leave the review surface and put focus back on the item that opened it. The
  // page variant's Blade restores focus itself; the in-blade swap re-shows the
  // list first, so focus the Review button on the next frame.
  const closeReview = useCallback(() => {
    const batchId = reviewing?.batchId;
    setReviewing(null);
    void reseed();
    if (batchId && variant === 'blade') {
      requestAnimationFrame(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-review-batch="${batchId}"]`)?.focus();
      });
    }
  }, [reviewing, reseed, variant]);

  // Cancel-run click. A canvas-bound item cancels the WHOLE owning workflow run
  // (high blast radius), so confirm that explicitly first; an ad-hoc/copilot run
  // only cancels itself, so go straight through.
  const requestCancel = useCallback(
    (item: AgentInboxItem) => {
      if (item.workflowRunId != null && item.workflowId != null) {
        setConfirmCancel(item);
      } else {
        void doCancel(item);
      }
    },
    [doCancel],
  );

  return (
    <>
      {variant === 'page' ? (
        <PageHeader
          title={typeof title === 'string' ? title : 'Action Required'}
          subtitle="Items that need your attention before automations can proceed."
          actions={
            <>
              <span className={`inbox-conn ${agent?.connected ? 'on' : 'off'}`}>
                {agent?.connected ? 'Live' : 'Offline'}
              </span>
              <Button fillMode="outline" onClick={() => void reseed()}>Refresh</Button>
            </>
          }
        />
      ) : (
        <header className="inbox-head">
          {title != null && <h1>{title}</h1>}
          <span className={`inbox-conn ${agent?.connected ? 'on' : 'off'}`}>
            {agent?.connected ? 'Live' : 'Offline'}
          </span>
          <Button fillMode="outline" onClick={() => void reseed()}>Refresh</Button>
        </header>
      )}

      {error && <div className="inbox-error" role="alert">{error}</div>}

      {items === null && <p className="inbox-muted">Loading…</p>}
      {items !== null && items.length === 0 && pulsarItems.length === 0 && (
        <p className="inbox-muted">Nothing needs your attention right now.</p>
      )}

      {/* Inside the Action Required blade the review replaces the list. */}
      {reviewing?.batchId && variant === 'blade' && (
        <div>
          <Button type="button" fillMode="flat" onClick={closeReview} style={{ marginBottom: 8 }}>
            ← Back to Action Required
          </Button>
          <ProposalReview key={reviewing.batchId} batchId={reviewing.batchId} role={role} narrow onChanged={() => void reseed()} />
        </div>
      )}

      <ul className="inbox-list" ref={listRef} hidden={reviewing != null && variant === 'blade'}>
        {pulsarItems.map((p) =>
          p.kind === 'batch' ? (
            <PulsarBatchCard
              key={`batch-${p.batchId}`}
              item={p}
              canApprove={approver}
              onReview={() => setReviewing(p)}
              onChanged={() => void reseed()}
            />
          ) : (
            <PulsarQuestionCard key={`question-${p.questionId}`} item={p} onChanged={() => void reseed()} />
          ),
        )}
        {(items ?? []).map((item) => (
          <li key={item.agentRunId} className="inbox-card">
            <div className="inbox-card-head">
              <span className={`inbox-badge ${item.status === 'NeedsApproval' ? 'approval' : 'input'}`}>
                {item.status === 'NeedsApproval' ? 'Approval' : 'Input'}
              </span>
              <span className="inbox-goal">{item.goal}</span>
              {item.workflowRunId && item.workflowId ? (
                // Workflow-launched run → its complete run history (steps/logs/errors)
                // on the History page, deep-linked to this run's detail. Closing the
                // host blade first so the history isn't hidden behind it.
                <Link
                  className="inbox-audit-link"
                  to={`/runs?workflowId=${item.workflowId}&runId=${item.workflowRunId}`}
                  onClick={() => onNavigate?.()}
                >
                  View run history
                </Link>
              ) : item.conversationId ? (
                // An AI Playground step → back to its conversation (standing-agents D17).
                <Link
                  className="inbox-audit-link"
                  to={`/ai-playground/c/${item.conversationId}`}
                  onClick={() => onNavigate?.()}
                >
                  Open conversation
                </Link>
              ) : (
                // Ad-hoc/copilot run → no workflow run exists; the agent audit is the
                // only history for it.
                <Link
                  className="inbox-audit-link"
                  to={`/ai-playground/history?runId=${item.agentRunId}`}
                  onClick={() => onNavigate?.()}
                >
                  View audit
                </Link>
              )}
            </div>

            <div className="inbox-card-meta">
              {item.workflowName && (
                <span className="inbox-meta-workflow" title="Originating workflow">
                  {item.workflowName}
                </span>
              )}
              {item.parkedUtc && (
                <time className="inbox-meta-time" dateTime={item.parkedUtc}>
                  {formatDateTime(item.parkedUtc)}
                </time>
              )}
            </div>

            <p className="inbox-prompt">{item.prompt}</p>

            {item.status === 'NeedsApproval' ? (
              <>
                {item.proposedToolName && (
                  <p className="inbox-proposed">
                    Proposed tool: <code>{item.proposedToolName}</code>
                  </p>
                )}
                {item.proposedActionJson && (
                  <div className="inbox-action">
                    <ArgsView
                      args={item.proposedActionJson}
                      label={item.proposedToolName ? `Arguments for ${item.proposedToolName}` : 'Proposed arguments'}
                    />
                  </div>
                )}
                <div className="inbox-actions">
                  <Button
                    themeColor="primary"
                    disabled={pendingId === item.agentRunId}
                    onClick={() => void respond(item, 'approve')}
                  >
                    Approve
                  </Button>
                  {/* Run-wide auto-approve is offered ONLY for ad-hoc/copilot runs.
                      A workflow-launched run that parked for approval means its
                      AgentActivity node author chose per-action review (an auto-approve
                      node would never park), so we don't let the inbox override that
                      intent run-wide — the operator can still approve each step. */}
                  {!item.workflowRunId && (
                    <Button
                      themeColor="primary"
                      fillMode="outline"
                      disabled={pendingId === item.agentRunId}
                      onClick={() => setConfirmFuture(item)}
                      title="Approve this action and auto-approve every later step for the rest of this run"
                      data-testid="inbox-approve-future-btn"
                    >
                      Approve all &amp; future
                    </Button>
                  )}
                  <Button
                    fillMode="outline"
                    disabled={pendingId === item.agentRunId}
                    onClick={() => void respond(item, 'deny')}
                  >
                    Deny
                  </Button>
                  <Button
                    fillMode="flat"
                    disabled={pendingId === item.agentRunId}
                    onClick={() => requestCancel(item)}
                    data-testid="inbox-cancel-btn"
                  >
                    Cancel run
                  </Button>
                </div>
              </>
            ) : (
              <div className="inbox-answer">
                <TextArea
                  rows={2}
                  value={answers[item.agentRunId] ?? ''}
                  placeholder="Type your answer…"
                  aria-keyshortcuts="Meta+Enter Control+Enter"
                  onChange={(e) =>
                    setAnswers((a) => ({ ...a, [item.agentRunId]: String(e.value ?? '') }))
                  }
                  onKeyDown={(e) => {
                    // ⌘/Ctrl+Enter sends; plain Enter keeps adding newlines. Mirror the
                    // Send button's guard so the shortcut can't fire while a response is
                    // in flight or with an empty answer.
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (pendingId !== item.agentRunId && (answers[item.agentRunId] ?? '').trim().length > 0) {
                        void respond(item, 'answer');
                      }
                    }
                  }}
                />
                <Button
                  themeColor="primary"
                  disabled={pendingId === item.agentRunId || (answers[item.agentRunId] ?? '').trim().length === 0}
                  onClick={() => void respond(item, 'answer')}
                >
                  Send
                </Button>
                <Button
                  fillMode="flat"
                  disabled={pendingId === item.agentRunId}
                  onClick={() => requestCancel(item)}
                  data-testid="inbox-cancel-btn"
                >
                  Cancel run
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {reviewing?.batchId && variant === 'page' && (
        <Blade title={`${reviewing.agentName} — proposals`} width="min(720px, 96vw)" onClose={closeReview}>
          <ProposalReview key={reviewing.batchId} batchId={reviewing.batchId} role={role} narrow onChanged={() => void reseed()} />
        </Blade>
      )}

      {confirmFuture && (
        <Dialog
          title="Approve all &amp; future for this task?"
          onClose={() => setConfirmFuture(null)}
          width={460}
        >
          <p style={{ fontSize: 13 }}>
            This approves the current action <strong>and auto-approves every later step</strong>{' '}
            for the rest of this run — including tools that haven&apos;t been proposed yet. The
            agent will not pause for approval again on this run.
          </p>
          <p style={{ fontSize: 13 }}>
            To review each step instead, use <strong>Approve</strong>.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setConfirmFuture(null)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={pendingId === confirmFuture.agentRunId}
              onClick={() => {
                const item = confirmFuture;
                setConfirmFuture(null);
                void respond(item, 'approve', true);
              }}
              data-testid="inbox-approve-future-confirm-btn"
            >
              Approve all &amp; future
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {confirmCancel && (
        <Dialog
          title="Cancel this workflow run?"
          onClose={() => setConfirmCancel(null)}
          width={460}
        >
          <p style={{ fontSize: 13 }}>
            This item was launched by{' '}
            <strong>{confirmCancel.workflowName ?? 'a workflow'}</strong>. Cancelling it cancels
            the <strong>entire workflow run</strong>, not just this step — any remaining work in
            that run stops.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setConfirmCancel(null)}>Keep run</Button>
            <Button
              themeColor="primary"
              disabled={pendingId === confirmCancel.agentRunId}
              onClick={() => {
                const item = confirmCancel;
                setConfirmCancel(null);
                void doCancel(item);
              }}
              data-testid="inbox-cancel-workflow-confirm-btn"
            >
              Cancel workflow run
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}

export default InboxPanel;
